import { z } from "zod";

import { TRAINING_LAUNCH_MAX_QUEUE_AGE_SECONDS } from "./training-launch-queue";
import {
  TRAINING_GPU_TYPES,
  TRAINING_NODE_COUNTS,
  TrainingConfigSchema,
  TrainingDeploymentModeSchema,
} from "./training.models";

export const TRAINING_ORIGINS = [
  "manual",
  "auto_train",
  "cron",
  "api",
] as const;

export type TrainingOrigin = (typeof TRAINING_ORIGINS)[number];

export const MAX_SWEEP_RECIPES = 4;

/**
 * Bare object shape for transports whose OpenAPI generator requires a plain
 * ZodObject input. Cross-field rules live on `StartTrainingExecutionSchema`,
 * which the training service always re-parses.
 */
export const StartTrainingExecutionObjectSchema = z.object({
  taskId: z.string().min(1).optional(),
  recipeId: z.string().optional(),
  recipeIds: z
    .array(z.string().min(1))
    .min(1)
    .max(MAX_SWEEP_RECIPES)
    .optional()
    .describe("Mutually exclusive with recipeId."),
  deploymentMode: TrainingDeploymentModeSchema.default("provider"),
  baseModelId: z.string().min(1).optional(),
  judgeModelId: z.string().min(1).optional(),
  trainingConfig: TrainingConfigSchema.optional(),
  // The GPU shape must stay absent when the caller omits it so the training
  // service can resolve it from the selected recipe; a parse-time default
  // would silently override every recipe's GPU setup.
  gpuType: z.enum(TRAINING_GPU_TYPES).optional(),
  numNodes: z
    .number()
    .int()
    .refine(
      (value): value is (typeof TRAINING_NODE_COUNTS)[number] =>
        (TRAINING_NODE_COUNTS as readonly number[]).includes(value),
      {
        message: `numNodes must be one of: ${TRAINING_NODE_COUNTS.join(", ")}`,
      },
    )
    .optional(),
  gpusPerNode: z.number().int().min(1).max(8).optional(),
  onlyRunOnCluster: z.boolean().optional(),
  launchQueueTimeoutSeconds: z
    .number()
    .int()
    .positive()
    .max(TRAINING_LAUNCH_MAX_QUEUE_AGE_SECONDS)
    .optional(),
  callbackBaseUrlOverride: z.string().url().optional(),
  debug: z.boolean().optional(),
});

export const StartTrainingExecutionSchema =
  StartTrainingExecutionObjectSchema.superRefine((execution, ctx) => {
    if (execution.recipeId !== undefined && execution.recipeIds !== undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "recipeId and recipeIds are mutually exclusive",
        path: ["recipeIds"],
      });
    }
  });

export function normalizeExecutionRecipeIds(execution: {
  recipeId?: string;
  recipeIds?: string[];
}): string[] {
  return [
    ...new Set(
      execution.recipeIds ?? (execution.recipeId ? [execution.recipeId] : []),
    ),
  ];
}

export const TeacherSamplesTrainingSourceSchema = z.object({
  kind: z.literal("teacher_samples"),
  /**
   * When the auto-train preparation claim was taken. Feeds the deterministic
   * training-job id: stable across redeliveries of one round (the claim
   * happens once per round), fresh for the next round's new claim.
   */
  claimedAt: z.string().datetime(),
  taskId: z.string().min(1),
  identityHash: z.string().min(1),
  schemaHash: z.string().nullable(),
  promptHash: z.string().nullable(),
  configHash: z.string().nullable(),
  teacherModel: z.string().nullable(),
  minSamples: z.number().nullable(),
  sampleCount: z.number().int().positive(),
  evalHoldoutSampleCount: z.number().int().positive().optional(),
  recipeSelection: z.enum(["auto", "explicit"]).optional(),
  /**
   * Consent gate for classifier-driven size exploration. Required (no default):
   * the auto-train caller always knows the config's allowSizeExploration value.
   * When false, a secondary recipe on the selection is ignored.
   */
  allowSizeExploration: z.boolean(),
  sampleSelectionEndDate: z.string().datetime(),
});

export type TeacherSamplesTrainingSource = z.infer<
  typeof TeacherSamplesTrainingSourceSchema
>;

export const StartTrainingInputSchema = z
  .object({
    teamId: z.string().min(1),
    projectId: z.string().min(1),
    name: z.string().min(1),
    origin: z.enum(TRAINING_ORIGINS),
    initiator: z.object({
      userId: z.string().min(1),
      isSuperAdmin: z.boolean(),
    }),
    source: z.discriminatedUnion("kind", [
      z.object({
        kind: z.literal("datasets"),
        trainingDatasetId: z.string().min(1),
        evalDatasetId: z.string().min(1),
        rubricId: z.string().min(1).optional(),
        rubricVersionId: z.string().min(1).nullish(),
        /**
         * Optional for a single job and required for a multi-recipe sweep so
         * retried submissions resolve to the same sweep and member jobs.
         */
        idempotencyKey: z.string().min(1).optional(),
      }),
      TeacherSamplesTrainingSourceSchema,
    ]),
    execution: StartTrainingExecutionSchema,
  })
  .superRefine((input, ctx) => {
    if (
      input.source.kind === "datasets" &&
      input.source.rubricId === undefined &&
      input.source.rubricVersionId !== undefined
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "source.rubricVersionId requires source.rubricId",
        path: ["source", "rubricVersionId"],
      });
    }

    if (
      input.source.kind === "datasets" &&
      (input.execution.recipeIds?.length ?? 0) > 1 &&
      input.source.idempotencyKey === undefined
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "source.idempotencyKey is required when execution.recipeIds has multiple recipes",
        path: ["source", "idempotencyKey"],
      });
    }
  });

export type StartTrainingInput = z.infer<typeof StartTrainingInputSchema>;
